import { ref } from '@vue/composition-api'
import * as XLSX from 'xlsx-js-style'
import { saveAs } from 'file-saver'

export function useExcelProcessor() {
  const isProcessing = ref(false)
  const message = ref('')

  // Function to determine row type based on the "Type" column
  const getRowType = (row, headers) => {
    const typeColumnIndex = headers.findIndex(header => 
      header && header.toString().toLowerCase() === 'type'
    )
    
    if (typeColumnIndex !== -1) {
      const typeValue = row[typeColumnIndex]
      
      // Handle empty type values
      if (!typeValue || typeValue.toString().trim() === '') {
        return 'default' // Empty type
      }
      
      const normalizedTypeValue = typeValue.toString().toLowerCase().trim()
      
      // Map type values to our CSS classes
      const typeMap = {
        'item': 'item',
        'itemremark': 'itemremark',
        'section': 'section',
        'subsection': 'subsection',
        'unit': 'unit',
        'itemcategory': 'itemcategory'
      }
      
      return typeMap[normalizedTypeValue] || 'default'
    }
    
    return 'default' // No type column found
  }

  // Function to add previous current item no. to non-item rows preceding item rows
  const addPreviousItemNumbers = (rows, headers, useItemNumber = false) => {
    // Find the appropriate column index
    let itemColumnIndex
    let dummyItemColumnIndex
    
    const targetColumnName = useItemNumber ? 'current item no.' : 'previous item no.'
    
    itemColumnIndex = headers.findIndex(header => 
      header && header.toString().toLowerCase() === targetColumnName
    )
    
    dummyItemColumnIndex = headers.findIndex(header => 
      header && header.toString().toLowerCase() === 'dummy item no.'
    )
    
    if (itemColumnIndex === -1) {
      console.warn(`Column "${targetColumnName}" not found in headers`)
      return rows
    }
    
    // First pass: identify which rows precede items and what their special names should be
    const rowMetadata = rows.map((row, index) => {
      const nextItemIndex = findNextItemOrSectionIndex(rows, index + 1)
      const precedesItem = nextItemIndex !== -1 && rows[nextItemIndex].type === 'item'
      return { precedesItem, nextItemIndex }
    })
    
    // Second pass: process rows in order
    let currentItemNumber = null
    let pendingItemRemarks = [] // Track consecutive itemremark rows
    const processedRows = []
    
    // Helper function to flush pending itemremark rows
    const flushItemRemarks = () => {
      if (pendingItemRemarks.length > 0 && currentItemNumber) {
        pendingItemRemarks.forEach((remarkInfo, index) => {
          const remarkRowData = [...remarkInfo.row.data]
          remarkRowData[itemColumnIndex] = `${currentItemNumber}_itemremark_${index + 1}`
          processedRows.push({
            ...remarkInfo.row,
            data: remarkRowData
          })
        })
        pendingItemRemarks = []
      }
    }
    
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]
      const rowData = [...row.data]
      const metadata = rowMetadata[i]
      
      if (row.type === 'item') {
        // Flush any pending itemremark rows before processing the new item
        flushItemRemarks()
        
        const itemNumber = rowData[itemColumnIndex] || null
        const dummyItemNumber = dummyItemColumnIndex !== -1 ? (rowData[dummyItemColumnIndex] || null) : null
        // Use itemNumber if available, otherwise use dummyItemNumber for currentItemNumber
        currentItemNumber = itemNumber || dummyItemNumber
        
        processedRows.push({
          ...row,
          data: rowData
        })
        
      } else if (row.type === 'section') {
        // Flush any pending itemremark rows before processing section
        flushItemRemarks()
        
        // Section resets everything
        currentItemNumber = null
        processedRows.push({
          ...row,
          data: rowData
        })
        
      } else {
        // This is a non-item, non-section row
        
        // itemremark always uses the previous item number with special format
        if (row.type === 'itemremark') {
          // Add to pending itemremark list (will be flushed later)
          pendingItemRemarks.push({ row, rowData })
        } else {
          // Flush pending itemremarks before processing this row
          flushItemRemarks()
          
          // Check if this row precedes an item
          if (metadata.precedesItem) {
            // This row precedes an item - calculate its special name
            const nextItem = rows[metadata.nextItemIndex]
            const nextItemData = nextItem.data
            const nextItemNumber = nextItemData[itemColumnIndex] || null
            const nextDummyItemNumber = dummyItemColumnIndex !== -1 ? (nextItemData[dummyItemColumnIndex] || null) : null
            const keyNumber = nextItemNumber || nextDummyItemNumber
            
            if (keyNumber) {
              // Count how many of this type precede the same item
              let count = 1
              for (let j = i - 1; j >= 0; j--) {
                if (rows[j].type === 'item' || rows[j].type === 'section') break
                if (rowMetadata[j].precedesItem && 
                    rowMetadata[j].nextItemIndex === metadata.nextItemIndex && 
                    rows[j].type === row.type) {
                  count++
                }
              }
              
              rowData[itemColumnIndex] = `${keyNumber}_${row.type}_${count}`
            } else if (currentItemNumber) {
              // No next item number, use current
              rowData[itemColumnIndex] = currentItemNumber
            }
          } else {
            // This row doesn't precede an item, use current item number
            if (currentItemNumber && 
                (row.type === 'unit' || row.type === 'itemcategory') &&
                !rowData[itemColumnIndex]) {
              rowData[itemColumnIndex] = currentItemNumber
            }
          }
          
          processedRows.push({
            ...row,
            data: rowData
          })
        }
      }
    }
    
    // Flush any remaining itemremark rows at the end
    flushItemRemarks()
    
    return processedRows
  }
  
  // Helper function to find the next item or section row
  const findNextItemOrSectionIndex = (rows, startIndex) => {
    for (let i = startIndex; i < rows.length; i++) {
      if (rows[i].type === 'item' || rows[i].type === 'section') {
        return i
      }
    }
    return -1
  }

  // Process Excel file
  const processExcelFile = async (file, isOldFile = false) => {
    try {
      const fileBuffer = await file.arrayBuffer()
      const workbook = XLSX.read(fileBuffer)
      
      // Get the first worksheet
      const firstSheetName = workbook.SheetNames[0]
      const worksheet = workbook.Sheets[firstSheetName]
      
      // Convert to JSON
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 })
      
      if (jsonData.length > 0) {
        // First row is headers
        const headers = jsonData[0]
        // Rest are data rows
        let processedData = jsonData.slice(1).map((row, index) => ({
          rowIndex: index + 2, // +2 because we start from row 2 in Excel (1-indexed + skipped header)
          data: row,
          type: getRowType(row, headers)
        }))
        
        // Add previous current item no.
        processedData = addPreviousItemNumbers(processedData, headers, isOldFile)
        
        return {
          headers,
          data: processedData,
          success: true
        }
      } else {
        return {
          headers: [],
          data: [],
          success: false,
          error: 'No data found in the Excel file'
        }
      }
    } catch (error) {
      return {
        headers: [],
        data: [],
        success: false,
        error: error.message
      }
    }
  }

  // Compare two rows to check if they are different and return difference details
  const compareRowsDetailed = (newRowData, oldRowData, newHeaders, oldHeaders) => {
    const differences = []
    const maxLength = Math.max(newRowData.length, oldRowData.length)
    
    // Find columns to ignore (Previous item no. and Current item no.)
    const ignoredNewColumns = []
    const ignoredOldColumns = []
    
    newHeaders.forEach((header, index) => {
      const normalizedHeader = header && header.toString().toLowerCase()
      if (normalizedHeader === 'previous item no.' || normalizedHeader === 'current item no.') {
        ignoredNewColumns.push(index)
      }
    })
    
    oldHeaders.forEach((header, index) => {
      const normalizedHeader = header && header.toString().toLowerCase()
      if (normalizedHeader === 'previous item no.' || normalizedHeader === 'current item no.') {
        ignoredOldColumns.push(index)
      }
    })
    
    for (let i = 0; i < maxLength; i++) {
      // Skip comparison for ignored columns
      if (ignoredNewColumns.includes(i) || ignoredOldColumns.includes(i)) {
        continue
      }
      
      const newVal = newRowData[i] || ''
      const oldVal = oldRowData[i] || ''
      
      if (newVal.toString().trim() !== oldVal.toString().trim()) {
        differences.push(i)
      }
    }
    
    return {
      isDifferent: differences.length > 0,
      differences: differences
    }
  }

  // Compare two rows to check if they are different (backward compatibility)
  const areRowsDifferent = (row1Data, row2Data, headers) => {
    return compareRowsDetailed(row1Data, row2Data, headers, headers).isDifferent
  }

  // Find matching row in old data based on current item no./previous item no.
  const findMatchingOldRow = (newRow, oldData, newHeaders, oldHeaders) => {
    // For itemremark type, use dummy item no. instead
    if (newRow.type === 'itemremark') {
      // Find "Dummy item no." column in new excel
      const newDummyItemColumnIndex = newHeaders.findIndex(header => 
        header && header.toString().toLowerCase() === 'dummy item no.'
      )
      
      // Find "Dummy item no." column in old excel
      const oldDummyItemColumnIndex = oldHeaders.findIndex(header => 
        header && header.toString().toLowerCase() === 'dummy item no.'
      )
      
      if (newDummyItemColumnIndex === -1 || oldDummyItemColumnIndex === -1) {
        return null
      }
      
      const newDummyItemValue = newRow.data[newDummyItemColumnIndex]
      if (!newDummyItemValue) return null
      
      // Find row in old data with matching dummy item no. and same type
      return oldData.find(oldRow => {
        return oldRow.data[oldDummyItemColumnIndex] === newDummyItemValue && 
               oldRow.type === newRow.type
      })
    }
    
    // For other types, use previous item no. / current item no.
    // Find "Previous item no." column in new excel
    const newItemColumnIndex = newHeaders.findIndex(header => 
      header && header.toString().toLowerCase() === 'previous item no.'
    )
    
    // Find "Current Item no." column in old excel
    const oldItemColumnIndex = oldHeaders.findIndex(header => 
      header && header.toString().toLowerCase() === 'current item no.'
    )
    
    if (newItemColumnIndex === -1 || oldItemColumnIndex === -1) {
      return null
    }
    
    const newItemValue = newRow.data[newItemColumnIndex]
    if (!newItemValue) return null
    
    // Find row in old data with matching current item no. and same type
    return oldData.find(oldRow => {
      return oldRow.data[oldItemColumnIndex] === newItemValue && 
             oldRow.type === newRow.type
    })
  }

  // Generate comparison output
  const generateComparisonOutput = (newData, oldData, newHeaders, oldHeaders) => {
    const outputRows = []
    
    // Filter out section and empty rows from comparison
    // Section rows: rows with type 'section'
    // Empty rows: rows with empty or missing type column (type 'default')
    const shouldIncludeInComparison = (row) => {
      return row.type !== 'section' && row.type !== 'default' && row.type !== ''
    }
    
    // Filter the data to exclude section and empty rows
    const filteredNewData = newData.filter(shouldIncludeInComparison)
    const filteredOldData = oldData.filter(shouldIncludeInComparison)
    
    // Filter headers to remove Type column and empty headers (same logic as DataTable)
    const filteredNewHeaders = newHeaders
      .filter((header) => {
        const normalizedHeader = header && header.toString().toLowerCase()
        return normalizedHeader !== 'type' && header !== null && header !== undefined && header !== ''
      })
    
    // Filter old headers the same way
    const filteredOldHeaders = oldHeaders
      .filter((header) => {
        const normalizedHeader = header && header.toString().toLowerCase()
        return normalizedHeader !== 'type' && header !== null && header !== undefined && header !== ''
      })
    
    // Create combined headers with Status and Type columns at the beginning
    // Transform headers to clearly indicate which file they come from
    const transformedNewHeaders = filteredNewHeaders.map(header => {
      const normalizedHeader = header && header.toString().toLowerCase()
      if (normalizedHeader === 'previous item no.') {
        return 'Previous item no. (New File)'
      }
      if (normalizedHeader === 'current item no.') {
        return 'Current item no. (New File)'
      }
      return header
    })
    
    // We need to include both the old file's "Current item no." column and new file's "Current item no." column
    let oldCurrentItemHeader = null
    let newCurrentItemHeader = null
    
    const oldCurrentItemIndex = oldHeaders.findIndex(header => 
      header && header.toString().toLowerCase() === 'current item no.'
    )
    
    const newCurrentItemIndex = newHeaders.findIndex(header => 
      header && header.toString().toLowerCase() === 'current item no.'
    )
    
    if (oldCurrentItemIndex !== -1) {
      oldCurrentItemHeader = 'Current item no. (Old File)'
    }
    
    if (newCurrentItemIndex !== -1) {
      newCurrentItemHeader = 'Current item no. (New File)'
    }
    
    // Create combined headers - include all item number columns if they exist
    let combinedHeaders = ['Status', 'Type']
    
    // Add the previous item no. from new file first
    const prevItemHeaderIndex = transformedNewHeaders.findIndex(header => 
      header === 'Previous item no. (New File)'
    )
    if (prevItemHeaderIndex !== -1) {
      combinedHeaders.push('Previous item no. (New File)')
    }
    
    // Add the current item no. from new file
    if (newCurrentItemHeader) {
      combinedHeaders.push('Current item no. (New File)')
    }
    
    // Add the current item no. from old file
    if (oldCurrentItemHeader) {
      combinedHeaders.push('Current item no. (Old File)')
    }
    
    // Add all other headers from new file (excluding the item no. columns we already added)
    transformedNewHeaders.forEach(header => {
      if (header !== 'Previous item no. (New File)' && header !== 'Current item no. (New File)') {
        combinedHeaders.push(header)
      }
    })
    
    // Add headers row
    outputRows.push({
      data: combinedHeaders,
      status: 'header',
      differences: []
    })
    
    // Create a map of old rows for quick lookup
    const oldRowMap = new Map()
    filteredOldData.forEach(oldRow => {
      // For itemremark type, use dummy item no.
      if (oldRow.type === 'itemremark') {
        const oldDummyItemColumnIndex = oldHeaders.findIndex(header => 
          header && header.toString().toLowerCase() === 'dummy item no.'
        )
        
        if (oldDummyItemColumnIndex !== -1) {
          const dummyItemValue = oldRow.data[oldDummyItemColumnIndex]
          if (dummyItemValue) {
            const key = `${dummyItemValue}_${oldRow.type}`
            oldRowMap.set(key, oldRow)
          }
        }
      } else {
        // For other types, use current item no.
        const oldItemColumnIndex = oldHeaders.findIndex(header => 
          header && header.toString().toLowerCase() === 'current item no.'
        )
        
        if (oldItemColumnIndex !== -1) {
          const itemValue = oldRow.data[oldItemColumnIndex]
          if (itemValue) {
            const key = `${itemValue}_${oldRow.type}`
            oldRowMap.set(key, oldRow)
          }
        }
      }
    })
    
    // Process new data first to maintain the new file's order
    const processedOldRows = new Set() // Track which old rows we've already processed
    
    filteredNewData.forEach(newRow => {
      let matchingOldRow = null
      let shouldProcessAsNew = false
      let matchKey = null
      let newPrevItemValue = null
      
      // For itemremark type, use dummy item no.
      if (newRow.type === 'itemremark') {
        const newDummyItemColumnIndex = newHeaders.findIndex(header => 
          header && header.toString().toLowerCase() === 'dummy item no.'
        )
        
        if (newDummyItemColumnIndex === -1) return
        
        const newDummyItemValue = newRow.data[newDummyItemColumnIndex]
        newPrevItemValue = newDummyItemValue
        
        if (newDummyItemValue) {
          // Look for matching old row using dummy item no.
          const key = `${newDummyItemValue}_${newRow.type}`
          matchKey = key
          matchingOldRow = oldRowMap.get(key)
          
          if (matchingOldRow) {
            processedOldRows.add(key)
          } else {
            shouldProcessAsNew = true
          }
        } else {
          // No dummy item no. - consider as new
          shouldProcessAsNew = true
        }
      } else {
        // For other types, use previous item no.
        const newPrevItemColumnIndex = newHeaders.findIndex(header => 
          header && header.toString().toLowerCase() === 'previous item no.'
        )
        
        if (newPrevItemColumnIndex === -1) return
        
        newPrevItemValue = newRow.data[newPrevItemColumnIndex]
        
        // Handle two cases for new items:
        // 1. Items with previous item no. that might match with old data
        // 2. Items with type 'item' but no previous item no. (always considered new)
        
        if (newPrevItemValue) {
          // Case 1: Has previous item no. - look for matching old row
          const key = `${newPrevItemValue}_${newRow.type}`
          matchKey = key
          matchingOldRow = oldRowMap.get(key)
          
          if (matchingOldRow) {
            processedOldRows.add(key)
          } else {
            shouldProcessAsNew = true
          }
        } else if (newRow.type === 'item') {
          // Case 2: No previous item no. but type is 'item' - always consider as new
          shouldProcessAsNew = true
        }
      }
      
      if (matchingOldRow) {
        // This row exists in both old and new data - check for changes
        
        // Get the item values for all three columns
        const newCurrentItemColumnIndex = newHeaders.findIndex(header => 
          header && header.toString().toLowerCase() === 'current item no.'
        )
        const oldCurrentItemColumnIndex = oldHeaders.findIndex(header => 
          header && header.toString().toLowerCase() === 'current item no.'
        )
        
        const newCurrentItemValue = newCurrentItemColumnIndex !== -1 ? newRow.data[newCurrentItemColumnIndex] || '' : ''
        const oldCurrentItemValue = oldCurrentItemColumnIndex !== -1 ? matchingOldRow.data[oldCurrentItemColumnIndex] || '' : ''
        
        // Filter row data excluding the item number columns (we'll handle them separately)
        const filteredNewRowData = newHeaders
          .map((header, index) => {
            const normalizedHeader = header && header.toString().toLowerCase()
            if (normalizedHeader !== 'type' && 
                normalizedHeader !== 'previous item no.' && 
                normalizedHeader !== 'current item no.' && 
                header !== null && header !== undefined && header !== '') {
              return newRow.data[index] || ''
            }
            return null
          })
          .filter(cell => cell !== null)
        
        // Filter old row data excluding the item number columns
        const filteredOldRowData = oldHeaders
          .map((header, index) => {
            const normalizedHeader = header && header.toString().toLowerCase()
            if (normalizedHeader !== 'type' && 
                normalizedHeader !== 'previous item no.' && 
                normalizedHeader !== 'current item no.' && 
                header !== null && header !== undefined && header !== '') {
              return matchingOldRow.data[index] || ''
            }
            return null
          })
          .filter(cell => cell !== null)
        
        // Pad or truncate old data to match new data length
        const paddedOldRowData = []
        for (let i = 0; i < filteredNewRowData.length; i++) {
          if (i < filteredOldRowData.length) {
            paddedOldRowData.push(filteredOldRowData[i])
          } else {
            paddedOldRowData.push('')
          }
        }
        
        // Calculate differences (start from index 5 to account for Status, Type, and all three item no. columns)
        const oldRowDifferences = []
        
        for (let i = 0; i < Math.min(filteredNewRowData.length, paddedOldRowData.length); i++) {
          const newVal = (filteredNewRowData[i] || '').toString().trim()
          const oldVal = (paddedOldRowData[i] || '').toString().trim()
          
          if (newVal !== oldVal) {
            oldRowDifferences.push(i + 5) // +5 to account for Status, Type, and all three item number columns
          }
        }
        
        // Construct row data with proper column order: Status, Type, Previous item no. (New File), Current item no. (New File), Current item no. (Old File), ...other data
        const constructRowData = (status, rowType, itemPrevValue, itemNewCurrentValue, itemOldCurrentValue, dataArray) => {
          return [status, rowType, itemPrevValue, itemNewCurrentValue, itemOldCurrentValue, ...dataArray]
        }
        
        // Only mark as "Changed" if there are actual visual differences to show
        if (oldRowDifferences.length > 0) {
          // Add the new row with "Changed" status - highlight the changed cells
          const newRowData = constructRowData('Changed', newRow.type, newPrevItemValue, newCurrentItemValue, oldCurrentItemValue, filteredNewRowData)
          outputRows.push({
            data: newRowData,
            status: 'Changed',
            differences: oldRowDifferences // Highlight changed cells in the "Changed" row
          })
          
          // Add the "Previous" row to show the old values - no highlighting
          const oldRowData = constructRowData('Previous', matchingOldRow.type, newPrevItemValue, newCurrentItemValue, oldCurrentItemValue, paddedOldRowData)
          outputRows.push({
            data: oldRowData,
            status: 'Previous',
            differences: [] // No highlighting on the "Previous" row
          })
        } else {
          // No visual differences to show, mark as "Unchanged"
          const unchangedRowData = constructRowData('Unchanged', newRow.type, newPrevItemValue, newCurrentItemValue, oldCurrentItemValue, filteredNewRowData)
          outputRows.push({
            data: unchangedRowData,
            status: 'Unchanged',
            differences: []
          })
        }
      } else if (shouldProcessAsNew) {
        // This is a completely new row
        const newCurrentItemColumnIndex = newHeaders.findIndex(header => 
          header && header.toString().toLowerCase() === 'current item no.'
        )
        const newCurrentItemValue = newCurrentItemColumnIndex !== -1 ? newRow.data[newCurrentItemColumnIndex] || '' : ''
        
        // Filter new row data excluding item number columns
        const filteredNewRowData = newHeaders
          .map((header, index) => {
            const normalizedHeader = header && header.toString().toLowerCase()
            if (normalizedHeader !== 'type' && 
                normalizedHeader !== 'previous item no.' && 
                normalizedHeader !== 'current item no.' && 
                header !== null && header !== undefined && header !== '') {
              return newRow.data[index] || ''
            }
            return null
          })
          .filter(cell => cell !== null)
        
        // Create differences array for highlighting new records
        // Only highlight the Status column (index 0) for new records
        const newRowDifferences = [0] // Highlight only the Status column
        
        // Construct new row with proper column order
        // Previous item no. (New File), Current item no. (New File), Current item no. (Old File)
        const newRowData = ['New', newRow.type, newPrevItemValue || '', newCurrentItemValue, '', ...filteredNewRowData]
        outputRows.push({
          data: newRowData,
          status: 'New',
          differences: newRowDifferences
        })
      }
    })
    
    // Now add any old rows that weren't processed (these are deleted items)
    filteredOldData.forEach(oldRow => {
      let key = null
      
      // For itemremark type, use dummy item no.
      if (oldRow.type === 'itemremark') {
        const oldDummyItemColumnIndex = oldHeaders.findIndex(header => 
          header && header.toString().toLowerCase() === 'dummy item no.'
        )
        
        if (oldDummyItemColumnIndex === -1) return
        
        const oldDummyItemValue = oldRow.data[oldDummyItemColumnIndex]
        if (!oldDummyItemValue) return
        
        key = `${oldDummyItemValue}_${oldRow.type}`
      } else {
        // For other types, use current item no.
        const oldItemColumnIndex = oldHeaders.findIndex(header => 
          header && header.toString().toLowerCase() === 'current item no.'
        )
        
        if (oldItemColumnIndex === -1) return
        
        const oldItemValue = oldRow.data[oldItemColumnIndex]
        if (!oldItemValue) return
        
        key = `${oldItemValue}_${oldRow.type}`
      }
      
      if (!processedOldRows.has(key)) {
        // This row exists in old data but not in new data - it's deleted
        const oldItemColumnIndex = oldHeaders.findIndex(header => 
          header && header.toString().toLowerCase() === 'current item no.'
        )
        const oldItemValue = oldItemColumnIndex !== -1 ? oldRow.data[oldItemColumnIndex] || '' : ''
        
        // Filter old row data excluding item number columns
        const filteredOldRowData = oldHeaders
          .map((header, index) => {
            const normalizedHeader = header && header.toString().toLowerCase()
            if (normalizedHeader !== 'type' && 
                normalizedHeader !== 'previous item no.' && 
                normalizedHeader !== 'current item no.' && 
                header !== null && header !== undefined && header !== '') {
              return oldRow.data[index] || ''
            }
            return null
          })
          .filter(cell => cell !== null)
        
        // Pad to match new data structure
        const paddedOldRowData = []
        const expectedLength = combinedHeaders.length - 5 // minus Status, Type, and all three item columns
        for (let i = 0; i < expectedLength; i++) {
          if (i < filteredOldRowData.length) {
            paddedOldRowData.push(filteredOldRowData[i])
          } else {
            paddedOldRowData.push('')
          }
        }
        
        // This is a deleted row - construct with proper column order
        // Previous item no. (New File), Current item no. (New File), Current item no. (Old File)
        const deletedRowData = ['Deleted', oldRow.type, '', '', oldItemValue, ...paddedOldRowData]
        outputRows.push({
          data: deletedRowData,
          status: 'Deleted',
          differences: [0] // Highlight only the Status column
        })
      }
    })
    
    return outputRows
  }

  // Validate that required comparison columns exist
  const validateComparisonColumns = (newHeaders, oldHeaders) => {
    const errors = []
    
    // Check for "Previous item no." in new Excel
    const newItemColumnExists = newHeaders.some(header => 
      header && header.toString().toLowerCase() === 'previous item no.'
    )
    
    if (!newItemColumnExists) {
      errors.push('New Excel file is missing the "Previous item no." column required for comparison')
    }
    
    // Check for "Current item no." in old Excel
    const oldItemColumnExists = oldHeaders.some(header => 
      header && header.toString().toLowerCase() === 'current item no.'
    )
    
    if (!oldItemColumnExists) {
      errors.push('Old Excel file is missing the "Current item no." column required for comparison')
    }
    
    return {
      isValid: errors.length === 0,
      errors: errors
    }
  }

  // Process files and generate output
  const processFiles = async (oldFile, newFile, oldFileData, newFileData, oldFileHeaders, newFileHeaders) => {
    if (!oldFile || !newFile) {
      message.value = 'Please upload both old and new Excel files before processing.'
      return { success: false }
    }
    
    isProcessing.value = true
    message.value = 'Validating comparison columns...'
    
    try {
      // Validate required comparison columns
      const validation = validateComparisonColumns(newFileHeaders, oldFileHeaders)
      
      if (!validation.isValid) {
        const errorMessage = 'Missing required comparison columns:\n' + validation.errors.join('\n')
        message.value = errorMessage
        return { 
          success: false, 
          error: errorMessage,
          missingColumns: validation.errors
        }
      }
      
      message.value = 'Comparing files and generating output...'
      
      // Show loading message
      await new Promise(resolve => setTimeout(resolve, 500))
      message.value = 'Analyzing differences between files...'
      
      // Generate comparison data
      const comparisonData = generateComparisonOutput(newFileData, oldFileData, newFileHeaders, oldFileHeaders)
      
      await new Promise(resolve => setTimeout(resolve, 1000))
      message.value = 'Comparison complete!'
      
      // Calculate statistics
      const dataRows = comparisonData.filter(row => row.status !== 'header')
      const totalRows = dataRows.length
      const newRows = dataRows.filter(row => row.status === 'New').length
      const changedRows = dataRows.filter(row => row.status === 'Changed').length
      const deletedRows = dataRows.filter(row => row.status === 'Deleted').length
      const unchangedRows = dataRows.filter(row => row.status === 'Unchanged').length
      
      message.value = `Comparison complete! Processed ${totalRows} rows (${newRows} new, ${changedRows} changed, ${deletedRows} deleted, ${unchangedRows} unchanged)`
      
      return {
        success: true,
        data: dataRows, // Remove header row
        headers: comparisonData[0].data, // Header row
        stats: {
          total: totalRows,
          new: newRows,
          changed: changedRows,
          deleted: deletedRows,
          unchanged: unchangedRows
        }
      }
    } catch (error) {
      console.error('Processing error:', error)
      message.value = `Processing error: ${error.message}`
      return { success: false, error: error.message }
    } finally {
      isProcessing.value = false
    }
  }

  // Load sample files
  const loadSampleFiles = async () => {
    try {
      message.value = 'Loading sample files...'
      
      // Load old sample file
      const oldSampleUrl = `/sample/case1_input_old.xlsx?t=${Date.now()}`
      const oldResponse = await fetch(oldSampleUrl)
      if (!oldResponse.ok) throw new Error('Failed to load old sample file')
      const oldArrayBuffer = await oldResponse.arrayBuffer()
      const oldSampleFile = new File([oldArrayBuffer], 'case1_input_old.xlsx', {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })
      
      // Load new sample file
      const newSampleUrl = `/sample/case1_input_new.xlsx?t=${Date.now()}`
      const newResponse = await fetch(newSampleUrl)
      if (!newResponse.ok) throw new Error('Failed to load new sample file')
      const newArrayBuffer = await newResponse.arrayBuffer()
      const newSampleFile = new File([newArrayBuffer], 'case1_input_new.xlsx', {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })
      
      // Process the sample files
      const oldResult = await processExcelFile(oldSampleFile, true)
      const newResult = await processExcelFile(newSampleFile, false)
      
      if (oldResult.success && newResult.success) {
        return {
          oldFile: oldSampleFile,
          newFile: newSampleFile,
          oldResult,
          newResult,
          success: true
        }
      } else {
        throw new Error('Failed to process sample files')
      }
    } catch (error) {
      console.error('Error loading sample files:', error)
      message.value = 'Failed to load sample files. You can upload your own files manually.'
      return { success: false, error: error.message }
    }
  }

  // Export data to Excel file
  const exportToExcel = async (data, headers) => {
    try {
      // Create a new workbook
      const wb = XLSX.utils.book_new()
      
      // Convert data to worksheet format
      // Headers should be the first row
      const wsData = [headers]
      
      // Add each data row
      data.forEach(row => {
        if (row.data && Array.isArray(row.data)) {
          wsData.push(row.data)
        }
      })
      
      // Create worksheet
      const ws = XLSX.utils.aoa_to_sheet(wsData)
      
      // Define color mappings for row types and statuses
      const rowTypeColors = {
        'item': 'E9C1C2',
        'itemremark': 'CFC6E1',
        'section': 'FFE5B4',
        'subsection': 'B2DAC3',
        'unit': 'F6E1C6',
        'itemcategory': 'C9D9ED',
        'default': 'FAFAFA'
      }
      
      const statusBorderColors = {
        'new': '28A745',
        'changed': 'FFC107',
        'previous': 'DC3545',
        'deleted': 'DC3545',
        'unchanged': '6C757D'
      }
      
      const changedCellColor = 'FFE6CC'
      const changedCellBorderColor = 'FF9500'
      
      // Add styling to header row
      if (ws['!ref']) {
        const headerRange = XLSX.utils.decode_range(ws['!ref'])
        for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
          const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col })
          if (!ws[cellAddress]) continue
          
          ws[cellAddress].s = {
            font: { bold: true, color: { rgb: "FFFFFF" } },
            fill: { fgColor: { rgb: "4472C4" } },
            alignment: { horizontal: "center", vertical: "center" },
            border: {
              top: { style: "thin", color: { rgb: "FFFFFF" } },
              bottom: { style: "thin", color: { rgb: "FFFFFF" } },
              left: { style: "thin", color: { rgb: "FFFFFF" } },
              right: { style: "thin", color: { rgb: "FFFFFF" } }
            }
          }
        }
        
        // Style data rows
        data.forEach((row, rowIndex) => {
          if (!row.data || !Array.isArray(row.data)) return
          
          const excelRowIndex = rowIndex + 1 // +1 because headers are row 0
          
          // Determine row type from the "Type" column (usually column index 1)
          let rowType = 'default'
          if (row.data[1] && typeof row.data[1] === 'string') {
            const typeValue = row.data[1].toLowerCase().trim()
            rowType = rowTypeColors[typeValue] ? typeValue : 'default'
          }
          
          // Get status and status color
          const status = row.status ? row.status.toLowerCase() : 'unchanged'
          const statusBorderColor = statusBorderColors[status] || '6C757D'
          
          // Apply styling to each cell in the row
          row.data.forEach((cellValue, colIndex) => {
            const cellAddress = XLSX.utils.encode_cell({ r: excelRowIndex, c: colIndex })
            if (!ws[cellAddress]) {
              ws[cellAddress] = { v: cellValue || '', t: 's' }
            }
            
            // Base cell style with row type background
            let cellStyle = {
              fill: { fgColor: { rgb: rowTypeColors[rowType] } },
              border: {
                top: { style: "thin", color: { rgb: "DEE2E6" } },
                bottom: { style: "thin", color: { rgb: "DEE2E6" } },
                left: colIndex === 0 ? { style: "thick", color: { rgb: statusBorderColor } } : { style: "thin", color: { rgb: "DEE2E6" } },
                right: { style: "thin", color: { rgb: "DEE2E6" } }
              },
              alignment: { vertical: "center" }
            }
            
            // Apply strikethrough for deleted rows (except status column which is usually the last one)
            if (status === 'deleted' && colIndex < row.data.length - 1) {
              cellStyle.font = { strike: true }
            }
            
            // Highlight cells with differences (includes status cells for New/Deleted records)
            if (row.differences && row.differences.includes(colIndex)) {
              cellStyle.fill = { fgColor: { rgb: changedCellColor } }
              cellStyle.border = {
                ...cellStyle.border,
                top: { style: "medium", color: { rgb: changedCellBorderColor } },
                bottom: { style: "medium", color: { rgb: changedCellBorderColor } },
                left: colIndex === 0 ? { style: "thick", color: { rgb: statusBorderColor } } : { style: "medium", color: { rgb: changedCellBorderColor } },
                right: { style: "medium", color: { rgb: changedCellBorderColor } }
              }
              cellStyle.font = { ...cellStyle.font, bold: true }
            }
            
            ws[cellAddress].s = cellStyle
          })
        })
      }
      
      // Add the worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Comparison Results')
      
      // Generate Excel file and trigger download
      const wbout = XLSX.write(wb, { 
        bookType: 'xlsx', 
        type: 'array',
        cellStyles: true 
      })
      
      const blob = new Blob([wbout], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      })
      
      const fileName = `template_comparison_${new Date().toISOString().slice(0, 10)}.xlsx`
      saveAs(blob, fileName)
      
      return { success: true }
    } catch (error) {
      console.error('Error exporting to Excel:', error)
      return { success: false, error: error.message }
    }
  }

  return {
    isProcessing,
    message,
    processExcelFile,
    processFiles,
    loadSampleFiles,
    exportToExcel,
    getRowType,
    addPreviousItemNumbers
  }
}
